/**
 * Pernod Ricard Kazakhstan · Marketing / Digital
 * Google Analytics 4 (GA4) & Google Tag Manager (GTM) integration helper
 * Brand: Ballantine's
 */

// Ru / Kz alphabet transliteration mapping for city & product slugs
const ruKzToEnMap = {
    а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'yo', ж: 'zh',
    з: 'z', и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o',
    п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f', х: 'kh', ц: 'ts',
    ч: 'ch', ш: 'sh', щ: 'shch', ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
    ә: 'a', ғ: 'g', қ: 'k', ң: 'n', ө: 'o', ұ: 'u', ү: 'u', һ: 'h', і: 'i'
};

/**
 * Transliterates Cyrillic text to a clean snake_case slug (e.g., 'Алматы' -> 'almaty')
 * @param {string} str 
 * @returns {string}
 */
export function toSlug(str) {
    if (!str || typeof str !== 'string') return '';
    return str
        .toLowerCase()
        .trim()
        .split('')
        .map(char => (ruKzToEnMap[char] !== undefined ? ruKzToEnMap[char] : char))
        .join('')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
}

/**
 * Pushes event/data payload to window.dataLayer
 * @param {Object} data 
 */
export function pushDataLayer(data) {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
    if (process.env.NODE_ENV !== 'production') {
        console.log('[Analytics dataLayer push]:', data);
    }
}

/**
 * Universal event dispatcher:
 * 1. Pushes { event: eventName, ...params } to window.dataLayer (for GTM/inspection)
 * 2. Calls window.gtag('event', eventName, params) (for GA4 direct hits via gtag.js)
 * @param {string} eventName 
 * @param {Object} [params] 
 */
export function trackEvent(eventName, params = {}) {
    pushDataLayer({
        event: eventName,
        ...params
    });

    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', eventName, params);
        if (process.env.NODE_ENV !== 'production') {
            console.log(`[Analytics gtag event: ${eventName}]:`, params);
        }
    }
}

// Global active landing metadata
let currentLandingMeta = {
    brand: 'ballantines',
    activation: 'ballantines_zvuk_2026',
    landing_id: 'ballantines-zvuk-2026',
    landing_type: 'registration'
};

/**
 * Задача 2: Идентификаторы активации (метаданные страницы) + отправка page_view
 * @param {Object} params
 * @param {string} params.brand - e.g., 'ballantines'
 * @param {string} params.activation - e.g., 'ballantines_zvuk_2026'
 * @param {string} params.landing_id - e.g., 'ballantines-zvuk-2026'
 * @param {string} params.landing_type - e.g., 'registration'
 */
export function initLanding({ brand, activation, landing_id, landing_type }) {
    currentLandingMeta = {
        brand: brand || 'ballantines',
        activation: activation || 'ballantines_zvuk_2026',
        landing_id: landing_id || 'ballantines-zvuk-2026',
        landing_type: landing_type || 'registration'
    };

    // 1. Push landing identifiers to dataLayer
    pushDataLayer(currentLandingMeta);

    // 2. Set dimensions globally in gtag so all subsequent events inherit them
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('set', currentLandingMeta);
    }

    // 3. Отправка события page_view при загрузке страницы с метаданными
    trackPageView();
}

/**
 * Отправляет событие page_view в GA4 и dataLayer
 * @param {Object} [customParams]
 */
export function trackPageView(customParams = {}) {
    const pageParams = {
        page_title: typeof document !== 'undefined' ? document.title : '',
        page_location: typeof window !== 'undefined' ? window.location.href : '',
        page_path: typeof window !== 'undefined' ? window.location.pathname : '',
        ...currentLandingMeta,
        ...customParams
    };

    trackEvent('page_view', pageParams);
}

// Track whether form_start has already been triggered on current page view
let formStarted = false;

/**
 * Сброс состояния form_start (при смене формы или маршрута)
 */
export function resetFormStart() {
    formStarted = false;
}

/**
 * Задача 3: Первый фокус / ввод в форму (один раз за сессию/загрузку)
 * @param {string} formId 
 */
export function trackFormStart(formId = 'main_registration') {
    if (formStarted) return;
    formStarted = true;
    trackEvent('form_start', {
        form_id: formId
    });
}

/**
 * Задача 3: Нажатие кнопки отправки формы
 * @param {string} formId 
 */
export function trackFormSubmit(formId = 'main_registration') {
    trackEvent('form_submit', {
        form_id: formId
    });
}

/**
 * Задача 3: Сообщение об ошибке валидации или сервера
 * Внимание: НИКОГДА не передает персональные данные пользователя!
 * @param {Object} params
 * @param {string} params.formId
 * @param {'validation'|'server'|'captcha'} params.errorType
 * @param {string} params.fieldName - e.g., 'phone', 'email', 'name', 'city'
 * @param {string} params.errorTextCode - e.g., 'phone_invalid_format', 'field_required'
 */
export function trackFormError({
    formId = 'main_registration',
    errorType = 'validation',
    fieldName,
    errorTextCode
}) {
    trackEvent('form_error', {
        form_id: formId,
        error_type: errorType,
        field_name: fieldName,
        error_text_code: errorTextCode
    });
}

/**
 * Задача 3: Успешная регистрация (только после подтверждения сервера/OTP)
 * @param {Object} params
 * @param {string} params.formId
 * @param {string} [params.city] - Человекочитаемое или slug название города
 * @param {string} [params.productChoice] - Название выбранного продукта/релиза
 */
export function trackRegistrationSuccess({
    formId = 'main_registration',
    city = '',
    productChoice = ''
}) {
    const payload = {
        form_id: formId
    };

    if (city) {
        payload.city = toSlug(city);
    }
    if (productChoice) {
        payload.product_choice = toSlug(productChoice);
    }

    trackEvent('registration_success', payload);
}

/**
 * Задача 3: Проверка возраста
 * @param {boolean} passed 
 */
export function trackAgeGate(passed) {
    trackEvent(passed ? 'age_gate_pass' : 'age_gate_fail', {
        user_choice: passed ? 'yes' : 'no'
    });
}

/**
 * Задача 4: Виртуальный просмотр страницы в SPA (экран «спасибо»)
 * @param {string} path - e.g., '/zvuk/thank-you'
 * @param {string} title - e.g., 'Спасибо за регистрацию'
 */
export function trackVirtualPageView(
    path = '/zvuk/thank-you',
    title = 'Спасибо за регистрацию'
) {
    trackEvent('virtual_page_view', {
        virtual_path: path,
        virtual_title: title
    });

    // Также отправляем page_view в GA4 с виртуальным URL
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'page_view', {
            page_title: title,
            page_location: window.location.origin + path,
            page_path: path,
            ...currentLandingMeta
        });
    }
}

/**
 * Обновляет состояние Google Consent Mode v2 без генерации события клика
 * @param {'granted'|'denied'} status
 */
export function updateConsentState(status = 'granted') {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('consent', 'update', {
            analytics_storage: status,
            ad_storage: status,
            ad_user_data: status,
            ad_personalization: status
        });
    }
}

/**
 * Задача 5: Пользователь нажал «Принять» на плашке согласия на cookies
 * 1. Обновляет Consent Mode v2 (analytics_storage, ad_storage -> granted)
 * 2. Пушит событие cookie_consent (с consent_analytics: granted, consent_marketing: granted)
 * 3. Отправляет page_view для полного учета сессии
 * @param {boolean} granted 
 */
export function trackCookieConsent(granted = true) {
    const status = granted ? 'granted' : 'denied';

    // 1. Обновление Consent Mode v2 в gtag
    updateConsentState(status);

    // 2. Пуш события cookie_consent в dataLayer и gtag
    trackEvent('cookie_consent', {
        consent_analytics: status,
        consent_marketing: status
    });

    // Совместимость с consent_update для обратной совместимости
    pushDataLayer({
        event: 'consent_update',
        analytics_consent: status,
        ads_consent: status
    });

    // 3. Если согласие получено, гарантированно логируем page_view с куками
    if (granted) {
        trackPageView();
    }
}

// Экспорт алиаса для обратной совместимости
export const trackConsent = trackCookieConsent;
